import { computed, type WritableComputedRef } from "vue";
import { useContentManager } from "./useContentManager";

/**
 * Inline building-block editor binding.
 *
 * Unlike the legacy dialog editors (which cloned a block into `localSettings` and
 * wrote the whole clone back on Save), inline editors bind their fields directly to
 * the live resource state via computed get/set. Each editor only ever patches the
 * top-level keys it OWNS, so the type editor + cross-cutting editors (Identity,
 * Validation, ...) can be mounted live at the same time without clobbering each
 * other (`updateContentById` is a shallow merge).
 */
export const useInlineBlockEditor = (id: string) => {
  const { getContentById, updateContentById } = useContentManager();

  const block = computed(() => getContentById(id));

  /** A computed get/set bound to a single owned top-level key. */
  const field = <T = any>(key: string, fallback?: T): WritableComputedRef<T> =>
    computed<T>({
      get: () => {
        const current = block.value?.[key];
        return (current === undefined ? fallback : current) as T;
      },
      set: (value: T) => {
        updateContentById(id, { [key]: value });
      },
    });

  /** Patch several owned keys at once. */
  const patch = (partial: Record<string, any>) => {
    updateContentById(id, { ...partial });
  };

  /** Read the current owned array (always returns a fresh array copy). */
  const readArray = (key: string): any[] => {
    const value = block.value?.[key];
    return Array.isArray(value) ? [...value] : [];
  };

  const addItem = (key: string, item: any, index?: number) => {
    const arr = readArray(key);
    if (typeof index === "number" && index >= 0 && index <= arr.length) {
      arr.splice(index, 0, item);
    } else {
      arr.push(item);
    }
    updateContentById(id, { [key]: arr });
  };

  const removeItem = (key: string, index: number) => {
    const arr = readArray(key);
    if (index < 0 || index >= arr.length) return;
    arr.splice(index, 1);
    updateContentById(id, { [key]: arr });
  };

  const moveItem = (key: string, from: number, to: number) => {
    const arr = readArray(key);
    if (from < 0 || from >= arr.length || to < 0 || to >= arr.length) return;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    updateContentById(id, { [key]: arr });
  };

  const updateItem = (key: string, index: number, itemPatch: Record<string, any>) => {
    const arr = readArray(key);
    if (index < 0 || index >= arr.length) return;
    arr[index] = { ...arr[index], ...itemPatch };
    updateContentById(id, { [key]: arr });
  };

  return {
    block,
    field,
    patch,
    addItem,
    removeItem,
    moveItem,
    updateItem,
  };
};
